import { 
    useState, 
    useEffect 
} from "react";

import { 
    useDispatch,
    useSelector 
} from "react-redux";

import {
    Form,
    InputGroup,
    Button,
    Row,
    Col,
    Badge,
    Modal
} from "react-bootstrap";

import 'bootstrap/dist/css/bootstrap.min.css';

import {
    showMsg,
    hideMsg,
    requestLogout,
    showLoading,
    hideLoading,
    pageSelector
} from "./store/pageSlice";

import {
    setCompanyConf,
    resetCompanyConf,
    setCompanyId,
    addConf,
    updateConf,
    deleteConf,
    updateContractStart,
    updateContractEnd,
    resetRefreshConfs,
    companyConfSelector
} from "./store/companyConfSlice";

import {
    FetchCompanyConf,
    GetConfig,
    SaveConfig
} from "./api/compConf";

import {
    FetchTrialLicense,
    GetTrialLicense,
    UpdateTrialLicense,
    ConvertToNonTrial,
    DeleteTrialLicense,
    AddTrialLicense,
    GetCompanyInfo
} from "./api/trialLicense";

import CompanyConfRow from "./CompanyConfRow";
import TrialModuleRow from "./TrialModuleRow";

import { FormatDateParam } from "./DateTimeFormat";

import "./App.css";

const defaultNewTrial = {
    companyName: "",
    productID: "",
    trialModule: [],
    dealerNo: "",
    serialNo: "",
    contractStartDate: "1970-01-01T00:00:00.000Z",
    contractEndDate: "1970-01-01T00:00:00.000Z",
    isRefreshMods: true
};

export default function CompanyConfPage(props) {
    const dispatch = useDispatch();

    const compConfSel = useSelector(companyConfSelector);
    const pageSel = useSelector(pageSelector);

    const [search, setSearch] = useState("");
    const [dsComp, setDsComp] = useState([]);
    const [compList, setCompList] = useState(null);
    
    const [qryRow, setQryRow] = useState(null);

    const [modList, setModList] = useState(null);
    const [showModList, setShowModList] = useState(false);

    const [confList, setConfList] = useState(null);
    const [showConf, setShowConf] = useState(false);

    const [newTrial, setNewTrial] = useState(defaultNewTrial);
    const [showNewTrial, setShowNewTrial] = useState(false);
    const [newTrialMods, setNewTrialMods] = useState(null);

    const submitSearch = async (e) => {
        e.preventDefault();

        dispatch(showLoading());

        try {
            let result = await FetchCompanyConf();

            if (!props.showTrial) {
                result = await FetchCompanyConf();
            } else {
                result = await FetchTrialLicense();
            }

            const srch = result.filter((row) => {
                const searchKey = search.toLowerCase();

                return row.companyName.toLowerCase().indexOf(searchKey) > -1 ||
                    row.companyID.toLowerCase().indexOf(searchKey) > -1 ||
                    row.productID.toLowerCase() === searchKey;
            });

            setDsComp(srch);
        } catch (ex) {
            console.error(ex.message);

            if (ex.status !== 401) {
                dispatch(showMsg({
                    title: "Error Company Configuration",
                    message: `An error occured during company filtering. ${ex.message}`
                }));
            } else {
                dispatch(requestLogout());
            }
        } finally {
            dispatch(hideLoading());
        }
    };

    const refreshList = async () => {
        dispatch(showLoading());

        try {
            dispatch(resetCompanyConf());

            let result;
            
            if (!props.showTrial) {
                result = await FetchCompanyConf();
            } else {
                result = await FetchTrialLicense();
            }
            
            setDsComp(result);
        } catch (ex) {
            console.error(ex.message);

            if (ex.status !== 401) {
                dispatch(showMsg({
                    title: "Error Company Configuration",
                    message: `Unable to fetch company listing. ${ex.message}`
                }));   
            } else {
                // request logout
                dispatch(requestLogout());
            }
        } finally {
            dispatch(hideLoading());
        }
    };

    const getContractDate = (AContract) => {
        let dt = new Date();

        if (AContract === "START") {
            dt = new Date(compConfSel.contractStartDate);
        } else {
            dt = new Date(compConfSel.contractEndDate);
        }

        return FormatDateParam(dt);
    };

    const setContractDate = (dt, AContract) => {
        if (AContract === "START") {
            dispatch(updateContractStart(dt));
        } else {
            dispatch(updateContractEnd(dt));
        }
    };

    const addNewConfig = () => {
        if (compConfSel.id === "-1") {
            return;
        }

        dispatch(addConf());
    };

    const renderSubModules = () => {
        if (compConfSel.subModules.length > 0) {
            const mods = compConfSel.subModules.map((row) => {
                return (
                    <tr className="tbody-row" key={ row.moduleID }>
                        <td className="tbody-cell">{ row.moduleID }</td>
                        <td className="tbody-cell">{ row.moduleName }</td>
                    </tr>
                );
            });

            setModList(mods);
        } else {
            setModList(null);
        }
    };

    const renderConfs = (confDat) => {
        const lst = [];
        
        for (let i = 0; i < confDat.length; i++) {
            const row = confDat[i];

            lst.push(
                <CompanyConfRow 
                    key={ row.key }
                    keyIndex={ i }
                    initialData={{
                        key: row.key,
                        value: row.value
                    }} 
                />
            );
        }

        setConfList(lst);
    };

    const onCancelChanges = () => {
        const companyName = qryRow.companyName;
        const productID = qryRow.productID;

        setQryRow(null);

        dispatch(resetCompanyConf());

        setQryRow({
            companyName: companyName,
            productID: productID
        });
    };

    const onSaveChanges = async () => {
        dispatch(showLoading());

        try {
            const compConf = {
                id: compConfSel.id,
                companyID: compConfSel.companyID,
                productID: compConfSel.productID,
                contractStartDate: compConfSel.contractStartDate,
                contractEndDate: compConfSel.contractEndDate,
                lastUpdate: compConfSel.lastUpdate,
                companyName: compConfSel.companyName,
                subModules: compConfSel.subModules,
                configurations: compConfSel.configurations,
            };

            if (!props.showTrial) {
                await SaveConfig(compConf);

                dispatch(showMsg({
                    title: "Configuration Saved",
                    message: "Configuration has been successfully saved."
                }));
            } else {
                await UpdateTrialLicense(compConf);

                dispatch(showMsg({
                    title: "Changes Saved",
                    message: "Changes to trial license has been successfully saved."
                }));
            }
        } catch (ex) {
            console.error(ex.message);

            if (ex.status !== 401) {
                dispatch(showMsg({
                    title: !props.showTrial ? "Error Company Configuration" : "Error Trial License",
                    message: `An error occured during ${!props.showTrial ? "trial license" : "company config."} selection. ${ex.message}`
                }));
            } else {
                // request logout
                dispatch(requestLogout());
            }
        } finally {
            dispatch(hideLoading());
        }
    };

    // ===== [Trial License] =====

    const requestConvertLicense = () => {
        dispatch(showMsg({
            title: "Covnert to Full-License",
            message: "Are you sure you want to convert selected trial-licence to full-license?",
            msgId: "CONV_TRIAL"
        }));
    };

    const requestDeleteLicense = () => {
        if (compConfSel.id === "-1") {
            return;
        }

        dispatch(showMsg({
            title: "Confirm Deletion",
            message: "Are you sure you want to delete selected license?",
            msgId: "DEL_TRIAL"
        }));
    };

    const convertLicense = async () => {
        dispatch(showLoading());

        try {
            const licn = {
                id: compConfSel.id,
                companyID: compConfSel.companyID,
                productID: compConfSel.productID,
                contractStartDate: compConfSel.contractStartDate,
                contractEndDate: compConfSel.contractEndDate,
                lastUpdate: compConfSel.lastUpdate,
                companyName: compConfSel.companyName,
                subModules: compConfSel.subModules,
                configurations: compConfSel.configurations,
            };

            await ConvertToNonTrial(licn);

            await refreshList();

            setQryRow(null);

            dispatch(resetCompanyConf());

            dispatch(showMsg({
                title: "Conversion Success",
                message: "License has been updated to full-license."
            }));
        } catch (ex) {
            console.error(ex.message);

            if (ex.status !== 401) {
                dispatch(showMsg({
                    title: "Error Trial License",
                    message: `An error occured during trial license deletion. ${ex.message}`
                }));
            } else {
                // request logout
                dispatch(requestLogout());
            }
        } finally {
            dispatch(hideLoading());
        }
    };

    const deleteTrial = async () => {
        dispatch(showLoading());

        try {
            await DeleteTrialLicense(compConfSel.id);

            await refreshList();

            setQryRow(null);

            dispatch(resetCompanyConf());

            dispatch(showMsg({
                title: "License Deleted",
                message: "License has been deleted."
            }));
        } catch (ex) {
            console.error(ex.message);

            if (ex.status !== 401) {
                dispatch(showMsg({
                    title: "Error Trial License",
                    message: `An error occured during trial license migrate. ${ex.message}`
                }));
            } else {
                // request logout
                dispatch(requestLogout());
            }
        } finally {
            dispatch(hideLoading());
        }
    };

    const onHideNewTrial = () => {
        setShowNewTrial(false);
    };

    const onTrialTextChange = (e, txtField) => {
        let newTrialDat = {
            ...newTrial
        };

        if (txtField === "companyName") {
            newTrialDat = {
                ...newTrialDat,
                companyName: e
            };
        } else if (txtField === "productID") {
            newTrialDat = {
                ...newTrialDat,
                productID: e
            };
        } else if (txtField === "dealerNo") {
            newTrialDat = {
                ...newTrialDat,
                dealerNo: e
            };
        } else if (txtField === "serialNo") {
            newTrialDat = {
                ...newTrialDat,
                serialNo: e
            };
        } else if (txtField === "contractStartDate") {
            newTrialDat = {
                ...newTrialDat,
                contractStartDate: e
            };
        } else if (txtField === "contractEndDate") {
            newTrialDat = {
                ...newTrialDat,
                contractEndDate: e
            };
        }

        setNewTrial(newTrialDat);
    };

    const addTrialSubModule = () => {
        const subs = newTrial.trialModule;

        if (subs.findIndex((row) => row.moduleID === "" && row.moduleName === "") > -1) {
            return;
        }

        subs.push({
            moduleID: "",
            moduleName: ""
        });
        
        const newTrialDat = {
            ...newTrial,
            trialModule: subs,
            isRefreshMods: true
        };

        setNewTrial(newTrialDat);
    };

    const removeTrialSubModule = (i) => {
        const subs = newTrial.trialModule;

        if (subs.length <= i) {
            return;
        }

        subs.splice(i, 1);

        setNewTrial({
            ...newTrial,
            trialModule: subs,
            isRefreshMods: true
        });
    };

    const addNewTrial = () => {
        setNewTrial({
            ...defaultNewTrial,
            isRefreshMods: true
        });

        setShowNewTrial(true);
    };

    const updateNewTrial = (e, txtField, i) => {
        const mods = newTrial.trialModule;

        let newMod = { ...mods[i] };

        if (txtField === "KEY") {
            newMod = {
                ...newMod,
                moduleID: e
            };
        } else if (txtField === "VALUE") {
            newMod = {
                ...newMod,
                moduleName: e
            };
        }

        mods.splice(i, 1, newMod);

        setNewTrial({
            ...newTrial,
            trialModule: mods
        });
    };

    const saveNewTrial = async (e) => {
        e.preventDefault();

        dispatch(showLoading());

        try {
            const licn = {
                companyName: newTrial.companyName,
                productID: newTrial.productID,
                trialModule: newTrial.trialModule,
                dealerNo: newTrial.dealerNo,
                serialNo: newTrial.serialNo,
                contractStartDate: new Date(newTrial.contractStartDate).toJSON(),
                contractEndDate: new Date(newTrial.contractEndDate).toJSON()
            };

            await AddTrialLicense(licn);

            await refreshList();

            setQryRow(null);

            dispatch(resetCompanyConf());

            setShowNewTrial(false);

            dispatch(showMsg({
                title: "New Trial License",
                message: "New trial license has been successfully registered."
            }));
        } catch (ex) {
            console.error(ex.message);

            if (ex.status !== 401) {
                dispatch(showMsg({
                    title: "Error Trial License",
                    message: `An error occured during registering new trial license. ${ex.message}`
                }));
            } else {
                // request logout
                dispatch(requestLogout());
            }
        } finally {
            dispatch(hideLoading());
        }
    };

    const getCompTrialInfo = async () => {
        try {
            const {
                dealerCode,
                serialNo
            } = await GetCompanyInfo(newTrial.companyName);

            let newTrialDat = {
                ...newTrial
            };

            if (dealerCode !== null) {
                newTrialDat = {
                    ...newTrial,
                    dealerNo: dealerCode
                };
            }

            if (serialNo !== null) {
                newTrialDat = {
                    ...newTrial,
                    serialNo: serialNo
                };
            }

            setNewTrial(newTrialDat);
        } catch (ex) {
            console.error(ex.message);

            if (ex.status !== 401) {
                dispatch(showMsg({
                    title: "Error Get Company Info",
                    message: `An error occured during getting company info. ${ex.message}`
                }));
            } else {
                // request logout
                dispatch(requestLogout());
            }
        } finally {
            dispatch(hideLoading());
        }
    };

    // ====================

    useEffect(() => {
        (
            async () => {
                dispatch(showLoading());

                setShowNewTrial(false);
                
                await refreshList();  
                
                dispatch(hideLoading());
            }
        )();
    }, [props.showTrial]);

    useEffect(() => {
        const lst = dsComp.map((row) => {
            return (
                <div 
                    key={ `${row.companyName}_${row.productID}` } 
                    className="list-item"
                    onClick={() => { setQryRow({
                        companyName: row.companyName,
                        productID: row.productID
                    }) }}
                    >
                    <strong>{ row.companyName }</strong>
                    <div className="list-item-section">
                        <Badge pill bg="primary">{ row.productID }</Badge>     
                    </div>      
                </div>
            );
        });

        setCompList(lst);
    }, [dsComp]);

    useEffect(() => {
        setShowConf(false);

        if (qryRow === null) {
            return;
        }

        (
            async () => {
                dispatch(showLoading());

                try {
                    let result;
                    
                    if (!props.showTrial) {
                        result = await GetConfig({
                            companyName: qryRow.companyName,
                            productID: qryRow.productID
                        });
                    } else {
                        result = await GetTrialLicense({
                            companyName: qryRow.companyName,
                            productID: qryRow.productID
                        });
                    }

                    if (result === null) {
                        dispatch(resetCompanyConf());
                        return;
                    }

                    setShowModList(false);

                    dispatch(setCompanyConf(result));
                } catch(ex) {
                    console.error(ex.message);
        
                    if (ex.status !== 401) {
                        dispatch(showMsg({
                            title: !props.showTrial ? "Error Company Configuration" : "Error Trial License",
                            message: `An error occured during company config. selection. ${ex.message}`
                        }));
                    } else {
                        // request logout
                        dispatch(requestLogout());
                    }
                } finally {
                    dispatch(hideLoading());
                }
            }
        )();
    }, [qryRow]);

    useEffect(() => {
        renderSubModules();        
    }, [compConfSel.id]);

    useEffect(() => {
        if (!compConfSel.isRefreshConfs) {
            return;
        }

        if (compConfSel.configurations !== undefined && compConfSel.configurations !== null) {
            renderConfs(compConfSel.configurations);
        } else {
            setConfList(null);
        }

        dispatch(resetRefreshConfs());
    }, [compConfSel.isRefreshConfs]);

    useEffect(() => {
        if (!pageSel.msgResult || pageSel.msgId === null) {
            return;
        }

        (
            async () => {
                if (pageSel.msgId == "CONV_TRIAL") {
                    dispatch(hideMsg());
                    await convertLicense();
                } else if (pageSel.msgId == "DEL_TRIAL") {
                    dispatch(hideMsg());
                    await deleteTrial();
                }
            }
        )();
    }, [pageSel.msgResult]);

    useEffect(() => {
        if (!newTrial.isRefreshMods) {
            return;
        }

        if (newTrial.trialModule.length > 0) {
            const mods = [];            
            const trMods = newTrial.trialModule;

            for (let i = 0; i < trMods.length; i++) {
                const row = trMods[i];

                mods.push(
                    <TrialModuleRow 
                        key={ row.key + "_" + i }
                        keyIndex={ i }
                        initialData={{
                            key: row.key,
                            value: row.value
                        }}
                        onDelete={ removeTrialSubModule }
                        onChange={ updateNewTrial }
                    />
                );
            }

            setNewTrialMods(mods);
        } else {
            setNewTrialMods(null);
        }

        setNewTrial({
            ...newTrial,
            isRefreshMods: false
        });
    }, [newTrial.isRefreshMods]);

    return (
        <>
            <div className="editor-panel">
                <div className="search-panel">
                    <Form onSubmit={ submitSearch }>
                        <InputGroup className="mb-3">
                                <Button variant="success" onClick={() => { refreshList() }}><img src="img/replay_circle_filled_white_36dp.svg" /></Button>
                                <Form.Control placeholder="Enter company name or company ID" onChange={(e) => { setSearch(e.target.value) }} />
                                <Button type="submit"><img src="img/search_white_36dp.svg" /></Button>
                        </InputGroup>
                    </Form>
                </div>       
                <div className="editor-section">
                    <strong>Details</strong>
                    {
                        props.showTrial && (
                            <Button onClick={ addNewTrial }>
                                <img className="button-icon" src="img/add_circle_white_36dp.svg" />
                                Create New Trial
                            </Button>
                        )
                    }
                </div>         
                <Form>                    
                    <div className="editor-body">
                        <Form.Group className="mb-3">
                            <Form.Label>Company Name</Form.Label>
                            <Form.Control readOnly={ true } value={ compConfSel.companyName } />
                        </Form.Group>
                        <Row className="mb-3">
                            <Form.Group as={Col}>
                                <Form.Label>Company ID</Form.Label>
                                <Form.Control value={ compConfSel.companyID } onChange={(e) => { dispatch(setCompanyId(e.target.value)) }}/>
                            </Form.Group>
                            <Form.Group as={Col}>
                                <Form.Label>Product ID</Form.Label>
                                <Form.Control readOnly={true} value={ compConfSel.productID } />
                            </Form.Group>
                        </Row>
                        <Row className="mb-3">
                            <Form.Group as={Col}>
                                <Form.Label>Contract Start</Form.Label>
                                <Form.Control readOnly={ compConfSel.id === "-1" } type="date" value={ getContractDate("START") } onChange={(e) => { setContractDate(e.target.value, "START") }} />
                            </Form.Group>
                            <Form.Group as={Col}>
                                <Form.Label>Contract End</Form.Label>
                                <Form.Control readOnly={ compConfSel.id === "-1" } type="date" value={ getContractDate("END") } onChange={(e) => { setContractDate(e.target.value, "END") }} />
                            </Form.Group>
                        </Row>
                        {
                            props.showTrial && compConfSel.id !== "-1" && (
                                <Row className="mb-3">
                                    <Col>
                                        <Button variant="success" onClick={ requestConvertLicense }>
                                            <img className="button-icon" src="img/update_white_24dp.svg" />
                                            Convert To Full License
                                        </Button>
                                    </Col>
                                    <Col>
                                        <Button variant="danger" onClick={ requestDeleteLicense }>
                                            <img className="button-icon" src="img/delete_outline_white_24dp.svg" />
                                            Delete
                                        </Button>
                                    </Col>
                                </Row>
                            )
                        }
                        <Row className="mb-3">
                            <Col>
                                {
                                    compConfSel.id !== "-1" && (
                                        <Button variant="success" onClick={() => { setShowConf(!showConf) }}>
                                            <img className="button-icon" src="img/settings_white_24dp.svg" />
                                            { showConf ? "Hide Configurations" : "Show Configuration" }
                                        </Button>
                                    )
                                }
                            </Col>
                            <Col>
                                {
                                    modList !== null && (
                                        <Button onClick={() => { setShowModList(!showModList) }}>
                                            <img className="button-icon" src="img/view_module_white_24dp.svg" />
                                            {showModList ? "Hide Submodules" : "Show Submodules"}
                                        </Button>
                                    )
                                }                        
                            </Col>
                        </Row>
                    </div> 
                    {
                        showModList && (
                            <>
                                <div className="editor-section">
                                    <strong>Submodules</strong>
                                </div>
                                <div className="editor-sub-body">
                                    <table>
                                        <thead>
                                            <tr>
                                                <th scope="col">Module ID</th>
                                                <th scope="col">Module Name</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            { modList }
                                        </tbody>
                                    </table>
                                </div>
                            </>
                        )
                    }    
                    {
                        showConf && (
                            <>
                                <div className="editor-section">
                                    <strong>Configurations</strong>
                                    <Button variant="success" onClick={ addNewConfig }><img src="img/add_circle_white_36dp.svg" /></Button>
                                </div>
                                <div className="editor-sub-body">
                                    <table>
                                        <thead>
                                            <tr>
                                                <th scope="col">Key</th>
                                                <th scope="col">Value</th>
                                                <th scope="col"></th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            { confList }
                                        </tbody>
                                    </table>
                                </div>
                            </>
                        )
                    }
                    {
                        compConfSel.isChangesMade && (
                            <div className="editor-sub-body">
                                <div className="editor-button-box">
                                    <Button variant="success" onClick={onSaveChanges}><img className="button-icon" src="img/save_white_36dp.svg" />Save</Button>
                                    <Button variant="danger" onClick={onCancelChanges}><img className="button-icon" src="img/cancel_white_36dp.svg" />Cancel</Button>
                                </div>
                            </div>
                        )
                    }
                </Form>
            </div>
            <div className="listing">
                { compList }
            </div>
            {
                props.showTrial && (
                    <Modal 
                        show={ showNewTrial } 
                        fullscreen="lg-down" 
                        size="lg" 
                        centered
                        onHide={ onHideNewTrial }
                    >
                        <Modal.Header closeButton>
                            <Modal.Title>New Trial License</Modal.Title>
                        </Modal.Header>
                        <Modal.Body>
                            <Form onSubmit={ saveNewTrial }>
                                <Row className="mb-3">
                                    <Col>
                                        <Form.Label>Company Name</Form.Label>
                                        <Form.Control required={ true } value={ newTrial.companyName } onChange={(e) => { onTrialTextChange(e.target.value, "companyName") }} />
                                    </Col>
                                </Row>
                                <Row className="mb-3">
                                    <Col xs>
                                        <Button onClick={ getCompTrialInfo }>Get Info</Button>
                                    </Col>
                                </Row>
                                <Form.Group className="mb-3">
                                    <Form.Label>Product ID</Form.Label>
                                    <Form.Control required={ true } value={ newTrial.productID } onChange={(e) => { onTrialTextChange(e.target.value, "productID") }} />
                                </Form.Group>
                                <Row className="mb-3">
                                    <Form.Group as={ Col }>
                                        <Form.Label>Dealer No.</Form.Label>
                                        <Form.Control required={ true } value={ newTrial.dealerNo } onChange={(e) => { onTrialTextChange(e.target.value, "dealerNo") }} />
                                    </Form.Group>
                                    <Form.Group as={ Col }>
                                        <Form.Label>Serial No.</Form.Label>
                                        <Form.Control required={ true } value={ newTrial.serialNo } onChange={(e) => { onTrialTextChange(e.target.value, "serialNo") }} />
                                    </Form.Group>
                                </Row>
                                <Row className="mb-3">
                                    <Form.Group as={ Col }>
                                        <Form.Label>Contract Start</Form.Label>
                                        <Form.Control required={ true } type="date"  value={ newTrial.contractStartDate } onChange={(e) => { onTrialTextChange(e.target.value, "contractStartDate") }} />
                                    </Form.Group>
                                    <Form.Group as={ Col }>
                                        <Form.Label>Contract End</Form.Label>
                                        <Form.Control required={ true } type="date" value={ newTrial.contractEndDate } onChange={(e) => { onTrialTextChange(e.target.value, "contractEndDate") }} />
                                    </Form.Group>
                                </Row>
                                <div className="editor-section editor-section-bordered">
                                    <strong>SubModules</strong>
                                    <Button variant="success" onClick={ addTrialSubModule }><img src="img/add_circle_white_36dp.svg" /></Button>
                                </div>
                                <div className="editor-sub-body">
                                    <table>
                                        <thead>
                                            <tr>
                                                <th scope="col">Module ID</th>
                                                <th scope="col">Module Name</th>
                                                <th scope="col"></th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            { newTrialMods }
                                        </tbody>
                                    </table>
                                </div>
                                <div className="editor-sub-body">
                                    <div className="editor-button-box">
                                        <Button variant="secondary" onClick={() => { setShowNewTrial(false) }}>Cancel</Button>
                                        <Button variant="success" type="submit">Save</Button>
                                    </div>
                                </div>
                            </Form>
                        </Modal.Body>
                    </Modal>
                )
            }
        </>
    );
}
